import React from "react";
import "./Home.css";

const Privacy = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Animated Background Blurs */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-[#4299f5] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 animate-fade-in-up" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Privacy Policy
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 animate-fade-in-up animation-delay-200">
            Your privacy matters to us. Learn how we protect your data.
          </p>
          <p className="text-sm text-gray-400 mt-4 animate-fade-in-up animation-delay-400">Last updated: November 19, 2025</p>
        </div>
      </section>

      {/* Content Section */}
      <div className="bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-2xl p-8 sm:p-12 border-2 border-transparent hover:border-[#4299f5] transition-all duration-300">

          <div className="space-y-8 text-gray-700">
            <section>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex items-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
                <span className="inline-block w-2 h-8 bg-[#4299f5] mr-3 rounded"></span>
                1. Introduction
              </h2>
            <p>
              Welcome to Trivana Hospitality ("we", "our", or "us"). We are committed to protecting your personal 
              information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, 
              and safeguard your information when you visit our website and use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex items-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
                <span className="inline-block w-2 h-8 bg-[#4299f5] mr-3 rounded"></span>
                2. Information We Collect
              </h2>
              <p className="mb-3">We collect information that you provide directly to us, including:</p>
              <ul className="list-none space-y-2 ml-0">
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong className="text-gray-900">Account Information:</strong> Name, email address, and password when you register</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong className="text-gray-900">Google OAuth Information:</strong> Name, email, and Google user ID when you sign in with Google</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong className="text-gray-900">Communication Data:</strong> Information from contact forms and email correspondence</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong className="text-gray-900">Usage Data:</strong> Information about how you use our website and services</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong className="text-gray-900">Technical Data:</strong> IP address, browser type, device information, and cookies</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex items-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
                <span className="inline-block w-2 h-8 bg-[#4299f5] mr-3 rounded"></span>
                3. How We Use Your Information
              </h2>
              <p className="mb-3">We use the information we collect to:</p>
              <ul className="list-none space-y-2 ml-0">
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Create and manage your account</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Provide, maintain, and improve our services</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Send you verification emails and account notifications</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Respond to your inquiries and provide customer support</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Send you updates about our services (with your consent)</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Detect, prevent, and address technical issues and security incidents</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Comply with legal obligations and enforce our terms</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex items-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
                <span className="inline-block w-2 h-8 bg-[#4299f5] mr-3 rounded"></span>
                4. Google OAuth and Third-Party Services
              </h2>
              <p>
                When you sign in with Google, we receive your basic profile information (name, email, profile picture) 
                from Google. We use this information solely for authentication purposes and account creation. Your use 
                of Google Sign-In is also subject to Google's Privacy Policy. We do not sell, rent, or share your 
                personal information with third parties for their marketing purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex items-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
                <span className="inline-block w-2 h-8 bg-[#4299f5] mr-3 rounded"></span>
                5. Data Security
              </h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal information, 
                including encryption of passwords using industry-standard BCrypt hashing, secure JWT token-based 
                authentication, and HTTPS encryption for data transmission. However, no method of transmission over 
                the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex items-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
                <span className="inline-block w-2 h-8 bg-[#4299f5] mr-3 rounded"></span>
                6. Data Retention
              </h2>
              <p>
                We retain your personal information for as long as necessary to provide our services and fulfill the 
                purposes outlined in this Privacy Policy. You may request deletion of your account at any time by 
                contacting us at the email address below.
              </p>
            </section>

            <section>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex items-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
                <span className="inline-block w-2 h-8 bg-[#4299f5] mr-3 rounded"></span>
                7. Cookies and Tracking Technologies
              </h2>
              <p>
                We use cookies and similar tracking technologies to track activity on our website and store certain 
                information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being 
                sent. However, if you do not accept cookies, you may not be able to use some portions of our service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex items-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
                <span className="inline-block w-2 h-8 bg-[#4299f5] mr-3 rounded"></span>
                8. Your Rights
              </h2>
              <p className="mb-3">You have the right to:</p>
              <ul className="list-none space-y-2 ml-0">
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Access, correct, or delete your personal information</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Object to or restrict processing of your data</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Data portability</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Withdraw consent at any time</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-[#4299f5] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Lodge a complaint with a supervisory authority</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex items-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
                <span className="inline-block w-2 h-8 bg-[#4299f5] mr-3 rounded"></span>
                9. Children's Privacy
              </h2>
              <p>
                Our services are not intended for children under the age of 13. We do not knowingly collect personal 
                information from children under 13. If you are a parent or guardian and believe your child has provided 
                us with personal information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex items-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
                <span className="inline-block w-2 h-8 bg-[#4299f5] mr-3 rounded"></span>
                10. Changes to This Privacy Policy
              </h2>
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the 
                new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this 
                Privacy Policy periodically for any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex items-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
                <span className="inline-block w-2 h-8 bg-[#4299f5] mr-3 rounded"></span>
                11. Contact Us
              </h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="mt-4 p-6 bg-gray-50 rounded-lg border-l-4 border-[#4299f5]">
                <ul className="list-none space-y-2">
                  <li className="flex items-center">
                    <span className="text-[#4299f5] font-bold mr-2">✉</span>
                    <span><strong className="text-gray-900">Email:</strong> trivanahospitality@gmail.com</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-[#4299f5] font-bold mr-2">🌐</span>
                    <span><strong className="text-gray-900">Website:</strong> trivanahospitality.com</span>
                  </li>
                </ul>
              </div>
            </section>
          </div>

          <div className="mt-10 pt-6 border-t-2 border-gray-200">
            <a 
              href="/" 
              className="inline-flex items-center text-[#4299f5] hover:text-[#3a8fe5] font-semibold text-lg transition-colors duration-200"
              style={{fontFamily: 'Montserrat, sans-serif'}}
            >
              <span className="mr-2">←</span> Back to Home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
